import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, CreditCard, User, GraduationCap, Calendar, Mail, Phone, MapPin, MessageSquare, Save } from "lucide-react";
import useAuth from "../../../contexts/AuthContext";
import Loader from "../../../common_components/Loader";
import { useToast } from "../../../common_components/Toast";
import { ApplicationStatus, PaymentStatus } from "../../../types/status";

export default function AlliedReviewApplication() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [application, setApplication] = useState<any>(null);
  const [reviewStatus, setReviewStatus] = useState<ApplicationStatus>(ApplicationStatus.SUBMITTED);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(PaymentStatus.PENDING);
  const [reviewComments, setReviewComments] = useState<string>("");

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const response = await apiRequest(`/applications/${id}`);
        const app = response.application || response;
        if (!app) throw new Error("Application details not found.");

        setApplication(app);
        setReviewStatus(app.status || ApplicationStatus.SUBMITTED);
        setPaymentStatus(app.payment?.status || PaymentStatus.PENDING);
        setReviewComments(app.reviewComments || "");
        console.log(app)
      } catch (err: any) {
        console.error("Error loading application:", err);
        setError(err.message || "Failed to load application data.");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadData();
    }
  }, [id, apiRequest]);

  const handleSaveReview = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiRequest(`/applications/admin/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: reviewStatus,
          paymentStatus: paymentStatus,
          reviewComments,
        }),
      });

      toast.success("Application review and status updated successfully!");
      navigate(-1);
    } catch (err: any) {
      console.error("Save review failed:", err);
      toast.error(err.message || "Failed to update review status.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader text="Loading application review..." />;

  if (error || !application) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md shadow-md text-center space-y-4">
          <XCircle className="w-16 h-16 text-red-600 mx-auto" />
          <h2 className="text-xl font-bold text-slate-800">Error Loading Application</h2>
          <p className="text-slate-600 text-sm">{error || "Application not found."}</p>
          <button onClick={() => navigate(-1)} className="w-full py-2 bg-slate-800 text-white font-semibold rounded-lg hover:bg-slate-900 transition">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { candidateName, email, mobile, category, gender, dob, neetRoll, neetAIRank, neetScore, intermediateSchool, preferenceOrder, payment, status } = application;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-serif font-bold text-slate-900">{candidateName}</h1>
                <span className="px-3 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-semibold uppercase tracking-wider">
                  {application.applicationId || id}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">Allied Health Sciences Admission Application Review</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 font-medium">Current Status:</span>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold uppercase">
              {status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content (Candidate Info) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Details Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
              <h2 className="text-lg font-serif font-bold text-slate-900 flex items-center gap-2 border-b pb-3 border-slate-100">
                <User className="w-5 h-5 text-indigo-600" /> Candidate Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">Category</label>
                  <span className="font-semibold text-slate-800">{category || "N/A"}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">Gender</label>
                  <span className="font-semibold text-slate-800">{gender || "N/A"}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">Date of Birth</label>
                  <span className="font-semibold text-slate-800">
                    {dob ? new Date(dob).toLocaleDateString("en-IN") : "N/A"}
                  </span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">Mobile Number</label>
                  <span className="font-semibold text-slate-800 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5 text-slate-400" /> {mobile || "N/A"}
                  </span>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-400 font-semibold block uppercase">Email Address</label>
                  <span className="font-semibold text-slate-800 flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5 text-slate-400" /> {email || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Academic & NEET Details Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
              <h2 className="text-lg font-serif font-bold text-slate-900 flex items-center gap-2 border-b pb-3 border-slate-100">
                <GraduationCap className="w-5 h-5 text-emerald-600" /> NEET & Academic Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">NEET Roll No.</label>
                  <span className="font-bold text-slate-900">{neetRoll || "N/A"}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">NEET All India Rank</label>
                  <span className="font-bold text-slate-900">{neetAIRank?.toLocaleString() || "N/A"}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">12th Percentage</label>
                  <span className="font-bold text-emerald-700">{intermediateSchool?.percentage ? `${intermediateSchool.percentage}%` : "N/A"}</span>
                </div>
              </div>

              {preferenceOrder && preferenceOrder.length > 0 && (
                <div className="pt-3 border-t border-slate-100">
                  <label className="text-xs text-slate-400 font-semibold block uppercase mb-2">Course Preferences</label>
                  <div className="flex flex-wrap gap-2">
                    {preferenceOrder.map((pref: string, idx: number) => (
                      <span key={idx} className="px-3 py-1 bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg break-words whitespace-normal max-w-full leading-snug">
                        Preference {idx + 1}: {pref}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Payment Info Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
              <h2 className="text-lg font-serif font-bold text-slate-900 flex items-center gap-2 border-b pb-3 border-slate-100">
                <CreditCard className="w-5 h-5 text-purple-600" /> Submitted Payment Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">Payment Status</label>
                  <span className="font-bold text-slate-800 uppercase">{payment?.status || "PENDING"}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">UTR / Ref Number</label>
                  <span className="font-bold text-slate-900">{payment?.utr || "N/A"}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block uppercase">Payment Date</label>
                  <span className="font-semibold text-slate-700">
                    {payment?.date ? new Date(payment.date).toLocaleString("en-IN") : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Decision Panel */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-md space-y-6 sticky top-6">
              <h2 className="text-lg font-serif font-bold text-slate-900 flex items-center gap-2 border-b pb-3 border-slate-100">
                <MessageSquare className="w-5 h-5 text-blue-600" /> Admin Review & Decision
              </h2>

              {/* Application Status Selection */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
                  Application Status *
                </label>
                <select
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value as ApplicationStatus)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold text-slate-800 bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value={ApplicationStatus.DRAFT}>DRAFT</option>
                  <option value={ApplicationStatus.SUBMITTED}>SUBMITTED</option>
                  <option value={ApplicationStatus.ACCEPTED}>ACCEPTED</option>
                  <option value={ApplicationStatus.REJECTED}>REJECTED</option>
                </select>
              </div>

              {/* Payment Status Selection */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
                  Payment Status *
                </label>
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold text-slate-800 bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value={PaymentStatus.PENDING}>PENDING</option>
                  <option value={PaymentStatus.PAID}>PAID</option>
                  <option value={PaymentStatus.VERIFIED}>VERIFIED</option>
                </select>
              </div>

              {/* Review Remarks / Reason */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
                  Review Remarks / Reason
                </label>
                <textarea
                  rows={4}
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  placeholder="Enter remarks or justification for status decision..."
                  className="w-full p-3 border border-slate-300 rounded-xl text-sm text-slate-800 bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveReview}
                disabled={saving}
                className="w-full py-3 px-4 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 active:scale-[0.99] transition shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving Changes..." : "Save Review Decision"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
